import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as contract from '@o11y-paas/contract';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import type { CreateOrgContract, OrgType, Region } from '@o11y-paas/contract';
import AlertBox from '../components/AlertBox';
import { config } from '../config';
import './Pages.css';

const regionMap: Record<string, string[]> = {
  dev: ['dev', 'dev2', 'test'],
  cnprod: ['cnprod'],
  default: ['euprod', 'naprod', 'skprod'],
};

const filteredRegion = regionMap[config.stack] ?? regionMap.default;

type CreateOrgForm = Omit<CreateOrgContract, 'region' | 'type'> & {
    region: '' | Region;
    type: '' | OrgType;
    leanix_cost_id: string;
}

const regionLabels = {
    dev: 'Development',
    dev2: 'Development 2',
    test: 'Testing',
    euprod: 'Europe',
    naprod: 'North America',
    skprod: 'South Korea',
    cnprod: 'China'
}

function CreateOrganization() {
    const { getAccessTokenSilently } = useAuth0();
    const [formData, setFormData] = useState<CreateOrgForm>({
        name: '',
        code: '',
        contact_email: '',
        region: '',
        type: '',
        leanix_cost_id: '',
        groups: [{ entra_group_name: '', role: contract.userRoles[0] }]
    });
    const [formValidationError, setFormValidationError] = useState<string>();
    const [response, setResponse] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [confirmationButtonType, setConfirmationButtonType] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { value } = e.target;
        const code = value.toLocaleLowerCase().replace(/[^a-zA-Z0-9]/g, '')
        setFormData(prevState => ({
            ...prevState,
            name: value,
            code
        }));
    };

    const handleGroupChange = (index: number, field: string, value: string) => {
        setFormData(prevState => {
            const updatedGroups = [...prevState.groups];
            updatedGroups[index] = {
                ...updatedGroups[index],
                [field]: value
            };
            return {
                ...prevState,
                groups: updatedGroups
            };
        });
    };

    const addGroup = () => {
        setFormData(prevState => ({
            ...prevState,
            groups: [...prevState.groups, { entra_group_name: '', role: 'viewer' }]
        }));
    };

    const removeGroup = (index: number) => {
        setFormData(prevState => ({
            ...prevState,
            groups: prevState.groups.filter((_, i) => i !== index)
        }));
    };

    const handleConfirmation = () => {
        setShowAlert(false);
        setConfirmationButtonType(false);
    };

    const handleAlertNo = () => {
        setShowAlert(false);
    };

    const handleAlertYes = () => {
        setShowAlert(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        const validation = await contract.createOrgContractValidator.safeParse(formData);
        if (!validation.success) {
            const errors = JSON.parse(validation.error.message)
            setFormValidationError(errors.map(e => e.message).join('\n'));
            return;
        }
        else {
            setFormValidationError('');
        }

        const token = await getAccessTokenSilently()
        
        // Handle form submission
        try {
            // Prepare data for submission - remove empty leanix_cost_id
            const submissionData = {
                ...formData,
                leanix_cost_id: formData.leanix_cost_id.trim() || undefined
            };
            
            const res = await fetch(`/api/orgs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(submissionData)
            });

            const isJson = res.headers.get('content-type')?.includes('application/json');
            const responseBody = isJson ? await res.json(): null;
    
            if (!res.ok) {
                const message =
                    responseBody?.error ||
                    responseBody?.fullError?.name ||
                    res.statusText ||
                    'An error occurred while creating the organization';

                throw new Error(`${message}`);
            }
    
            setResponse(responseBody);
        }
        catch (error) {
            setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
            setConfirmationButtonType(true);
            setShowAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (response) {
        return (
            <div className="page-container">
                <h1>Organization Created Successfully!</h1>
                <div className="response-section">
                    <h3>Organization Configuration</h3>
                    <div className="response-grid">
                        <div className="response-item">
                            <label>Org Id:</label>
                            <div className="value-container">
                                <span>{response.orgId}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.orgId) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Org Name:</label>
                            <div className="value-container">
                                <span>{formData.name}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(formData.name) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>X-Scope-OrgId:</label>
                            <div className="value-container">
                                <span>{response['X-Scope-OrgID']}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response['X-Scope-OrgID']) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Service Account:</label>
                            <div className="value-container">
                                <span>{response.serviceAccount}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.serviceAccount) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Service Token:</label>
                            <div className="value-container">
                                <span>{response.serviceToken}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.serviceToken) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Tempo DataSource UID:</label>
                            <div className="value-container">
                                <span>{response.tempoDataSourceUID}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.tempoDataSourceUID) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Loki DataSource UID:</label>
                            <div className="value-container">
                                <span>{response.lokiDataSourceUID}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.lokiDataSourceUID) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Mimir DataSource UID:</label>
                            <div className="value-container">
                                <span>{response.mimirDataSourceUID}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.mimirDataSourceUID) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>AlertManager DataSource UID:</label>
                            <div className="value-container">
                                <span>{response.alertManagerDataSourceUID}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.alertManagerDataSourceUID) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        <div className="response-item">
                            <label>Grafana URL:</label>
                            <div className="value-container">
                                <span>{response.grafanaUrl}</span>
                                <button
                                    className="copy-button"
                                    onClick={() => { navigator.clipboard.writeText(response.grafanaUrl) }}
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                    </div>
                    <button
                        className="submit-button"
                        onClick={() => {
                            setResponse(null);
                            setFormData({
                                name: '',
                                code: '',
                                contact_email: '',
                                region: '',
                                type: '',
                                leanix_cost_id: '',
                                groups: [{ entra_group_name: '', role: contract.userRoles[0] }]
                            });
                            setFormValidationError('');
                        }}
                    >
                        Create Another Organization
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <h1>Create Organization</h1>
            <p>Please fill out the form below to create a new organization:</p>

            <form onSubmit={handleSubmit} className="organization-form">
                <div className="form-group">
                    <label htmlFor="name">Organization Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleNameChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="code">Organization Code</label>
                    <input
                        type="text"
                        id="code"
                        name="code"
                        value={formData.code}
                        required
                        disabled

                    />
                    <small>You'll send this code alongside your application telemetery as an "X-Scope-OrgId" header</small>
                </div>
                <div className="form-group">
                    <label htmlFor="type">Application Type</label>
                    <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        required
                    >
                        <option value="" disabled selected hidden>Select an application type</option>
                        {contract.orgTypes.map(t =>
                            <option value={t}>{t}</option>
                        )}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="contact_email">Contact Email</label>
                    <input
                        type="contact_email"
                        id="contact_email"
                        name="contact_email"
                        value={formData.contact_email}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="leanix_cost_id">LeanIX Cost ID (Optional)</label>
                    <input
                        type="text"
                        id="leanix_cost_id"
                        name="leanix_cost_id"
                        value={formData.leanix_cost_id}
                        onChange={handleChange}
                        placeholder="Enter LeanIX Cost ID if applicable"
                    />
                    <small>Optional: Enter the LeanIX Cost ID for cost tracking purposes</small>
                </div>
                <div className="form-group">
                    <label htmlFor="region">Region</label>
                    <select
                        id="region"
                        name="region"
                        value={formData.region}
                        onChange={handleChange}
                        required
                    >
                        <option value="" disabled selected hidden>Select a region</option>
                        {filteredRegion.map(region =>
                            <option value={region}>{regionLabels[region]}</option>
                        )}
                    </select>
                </div>
                <div className="groups-section">
                    <h4>Groups and Roles</h4>
                    <p>Map Microsoft Entra Security Groups to Grafana roles. You can add multiple groups.</p>
                    {formData.groups.map((group, index) => (
                        <div key={index} className="group-row">
                            <div className="form-group">
                                <label htmlFor={`group-${index}`}>Group Name</label>
                                <input
                                    type="text"
                                    id={`group-${index}`}
                                    value={group.entra_group_name}
                                    onChange={(e) => handleGroupChange(index, 'entra_group_name', e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor={`role-${index}`}>Role</label>
                                <select
                                    id={`role-${index}`}
                                    value={group.role}
                                    onChange={(e) => handleGroupChange(index, 'role', e.target.value)}
                                >
                                    {contract.userRoles.map(r =>
                                        <option value={r}>{r}</option>
                                    )}
                                </select>
                            </div>
                            {
                                index > 0 &&
                                <button
                                    className="remove-group-button"
                                    onClick={() => removeGroup(index)}
                                >
                                    X
                                </button>
                            }
                        </div>
                    ))}
                    <button
                        className="add-group-button"
                        onClick={addGroup}
                    >
                        Add Another Group
                    </button>
                </div>
                {formValidationError && (<div
                    className='error'>
                    {formValidationError}
                </div>)}
                <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Creating Organization...' : 'Create Organization'}
                </button>
            </form>
            <AlertBox
                show={showAlert}
                message={alertMessage}
                onClose={confirmationButtonType ? handleConfirmation : handleAlertNo}
                onConfirm={!confirmationButtonType ? handleAlertYes : undefined}
                type="error"
            />
        </div>
    );
}

export default CreateOrganization;